/** Plan editor inserts from one live AI-answer card. Never returns the full transcript. */

import type {Article} from '../model/Article';
import type {AiAnswerSnapshot} from '../model/AiWritingBrief';
import {
    humanizeAiAnswerMarkdown,
    liveAnswerBody,
    normalizeBriefPhrase,
    textContainsPhrase,
} from './aiBriefCopy';

export const MAX_LIVE_WORK_IN = 8;
export const MAX_LIVE_WORK_IN_FACTS = 6;
export const MAX_LIVE_WORK_IN_QUESTIONS = 2;

const STOPWORDS = new Set([
    'this', 'that', 'with', 'from', 'have', 'will', 'your', 'about', 'into', 'more',
    'than', 'when', 'which', 'what', 'there', 'their', 'them', 'then', 'also',
    'such', 'only', 'just', 'over', 'after', 'before', 'other', 'some', 'most',
    'tato', 'toto', 'tohle', 'také', 'nebo', 'jako', 'podle', 'mezi',
    'pokud', 'proto', 'který', 'která', 'které', 'kterou', 'jejich', 'jeho', 'její',
    'když', 'tedy', 'proč', 'jaké', 'jaký', 'jaká',
    'and', 'the', 'for', 'are', 'not', 'can', 'you', 'but',
]);

export type LiveWorkInPlan = {
    lead: string;
    introFacts: string[];
    headingFacts: Array<{index: number; sentence: string}>;
    questions: string[];
};

type ArticleLike = {
    h1?: Array<{h?: string; p?: string}>;
    h2?: Array<{h?: string; p?: string}>;
    h3?: Array<{h?: string; p?: string}>;
    h4?: Array<{h?: string; p?: string}>;
    h5?: Array<{h?: string; p?: string}>;
    h6?: Array<{h?: string; p?: string}>;
    questions?: Array<{question?: string}>;
};

const KNOWN_ABBREV = new Set([
    'např', 'napr', 'atd', 'apod', 'tj', 'tzn', 'mld', 'tis', 'mil', 'ks', 'kg', 'km',
    'tzv', 'resp', 'mj', 'čl', 'cl', 'str', 'obr', 'ing', 'dr', 'p', 'r', 'sv',
]);

export function looksLikeLiveQuestion(text: string): boolean {
    const value = String(text || '').replace(/\s+/g, ' ').trim();
    if (!value) {
        return false;
    }
    if (/[?¿？]/.test(value)) {
        return true;
    }
    return /^(co |jak |pro[cč] |kdy |kde |kdo |kter |kolik |kam |[cč][ií]m |zda |je |jsou |k [cč]emu |na co |who |what |how |why |when |which |does |do |did |is |are |can |should |will |would |could )/i.test(value + ' ');
}

export function asWorkInSentence(text: string): string {
    const value = String(text || '').replace(/\s+/g, ' ').trim();
    if (!value || !isUsableWorkInSentence(value)) {
        return '';
    }
    return /[.!?…]$/.test(value) ? value : value + '.';
}

function lastTokenBeforePeriod(text: string): string {
    const match = String(text || '').trim().match(/([a-záäčďéěíĺľňóôřšťúůýž]{1,8})\.$/i);
    return match ? match[1].toLowerCase() : '';
}

/** Skip truncated "ve vyh." / "billb." fragments and broken "a ktivit" splits. */
export function looksTruncatedWorkInFragment(text: string): boolean {
    const value = String(text || '').replace(/\s+/g, ' ').trim();
    if (!value) {
        return true;
    }
    if (/\ba\s+ktivit/i.test(value)) {
        return true;
    }
    if (/\b(ve vyh|billb|jiných akt|jinych akt)\b/i.test(value)) {
        return true;
    }
    if (/\b(ve|v|na|do|pro|při|pri|z|s|o|u)\s+[a-záäčďéěíĺľňóôřšťúůýž]{2,5}\.$/i.test(value)) {
        const token = lastTokenBeforePeriod(value);
        if (token && token === token.toLowerCase() && !KNOWN_ABBREV.has(token) && token.length <= 4) {
            return true;
        }
    }
    return false;
}

function looksLikeKeywordListItem(text: string): boolean {
    const value = String(text || '').replace(/^\s*[-*•]\s+/, '').replace(/\s+/g, ' ').trim();
    if (!value) {
        return true;
    }
    if (/^[-*•]\s+\S{1,24}$/.test(String(text || '').trim())) {
        return true;
    }
    const words = wordCount(value);
    if (words <= 3 && !/\d/.test(value) && !/[.!?…]/.test(value) && !looksLikeLiveQuestion(value)) {
        return true;
    }
    return false;
}

export function isUsableWorkInSentence(text: string): boolean {
    const value = String(text || '').replace(/\s+/g, ' ').trim();
    if (!value || looksTruncatedWorkInFragment(value) || looksLikeKeywordListItem(value)) {
        return false;
    }
    return wordCount(value) >= 5 || isConcreteFact(value);
}

function isConcreteFact(text: string): boolean {
    return /\d/.test(text) || /[%€$£]|procent|percent|mld\.?\b|tisíc|tis\./i.test(text);
}

export function splitLiveAnswerSentences(text: string): string[] {
    const readable = humanizeAiAnswerMarkdown(text);
    const seen = new Set<string>();
    const result: string[] = [];
    for (const line of readable.split(/\n+/)) {
        const isList = /^\s*[-*•]\s+/.test(line) || /^\s*\d+[.)]\s+/.test(line);
        const cleaned = line
            .replace(/^\s*[-*•]\s+/, '')
            .replace(/^\s*\d+[.)]\s+/, '')
            .replace(/\s+/g, ' ')
            .trim();
        if (!cleaned) {
            continue;
        }
        if (isList && looksLikeKeywordListItem(line) && !isConcreteFact(cleaned)) {
            continue;
        }
        const pieces = /[.!?…]/.test(cleaned)
            ? cleaned.split(/(?<=[.!?…])\s+/)
            : [cleaned];
        for (const piece of pieces) {
            const sentence = piece.replace(/\s+/g, ' ').trim();
            const key = normalizeBriefPhrase(sentence);
            if (!sentence || !key || seen.has(key)) {
                continue;
            }
            if (looksTruncatedWorkInFragment(sentence) && !isConcreteFact(sentence)) {
                continue;
            }
            seen.add(key);
            result.push(sentence);
        }
    }
    return result;
}

function wordCount(text: string): number {
    return String(text || '').trim().split(/\s+/).filter(Boolean).length;
}

export function extractLiveLeadClaim(text: string): string {
    for (const part of splitLiveAnswerSentences(text)) {
        if (looksLikeLiveQuestion(part) || /[:：]\s*$/.test(part)) {
            continue;
        }
        const words = wordCount(part);
        if (words < 6 || words > 42 || looksTruncatedWorkInFragment(part)) {
            continue;
        }
        const lead = asWorkInSentence(part);
        if (lead) {
            return lead;
        }
    }
    return '';
}

export function extractLiveQuestions(text: string, limit = MAX_LIVE_WORK_IN_QUESTIONS): string[] {
    const found: string[] = [];
    const seen = new Set<string>();
    for (const part of splitLiveAnswerSentences(text)) {
        if (!looksLikeLiveQuestion(part)) {
            continue;
        }
        const question = part.replace(/\s+/g, ' ').trim().replace(/[.]+$/g, '');
        const words = wordCount(question);
        if (words < 3 || question.length > 140) {
            continue;
        }
        const key = normalizeBriefPhrase(question);
        if (!key || seen.has(key)) {
            continue;
        }
        seen.add(key);
        found.push(question);
        if (found.length >= limit) {
            break;
        }
    }
    return found;
}

function distinctiveTokens(text: string): string[] {
    return (String(text || '').toLowerCase().match(/[a-záäčďéěíĺľňóôřšťúůýž]{4,}/gi) || [])
        .map((item) => item.toLowerCase())
        .filter((item) => !STOPWORDS.has(item));
}

function tokensShareStem(left: string, right: string): boolean {
    if (left === right) {
        return true;
    }
    const len = Math.min(left.length, right.length);
    if (len < 5) {
        return false;
    }
    const stemLen = Math.min(6, len);
    return left.slice(0, stemLen) === right.slice(0, stemLen);
}

export function headingOverlapScore(heading: string, sentence: string): number {
    const title = String(heading || '').replace(/\s+/g, ' ').trim();
    if (!title || !sentence) {
        return 0;
    }
    if (title.length >= 8 && (textContainsPhrase(sentence, title) || textContainsPhrase(title, sentence))) {
        return 10;
    }
    const headingTokens = distinctiveTokens(title);
    if (!headingTokens.length) {
        return 0;
    }
    const sentenceTokens = distinctiveTokens(sentence);
    const overlap = headingTokens.filter((token) => sentenceTokens.some((item) => tokensShareStem(token, item)));
    if (!overlap.length) {
        return 0;
    }
    if (headingTokens.length <= 2) {
        return overlap.some((token) => token.length >= 6) ? overlap.length : 0;
    }
    return overlap.length >= 2 ? overlap.length : 0;
}

export function findRelatedHeadingTarget(article: ArticleLike, sentence: string): {contentPart: 'h2'; index: number; tag: 'p'} | null {
    const headings = article.h2 || [];
    let best: {index: number; score: number} | null = null;
    for (let index = 0; index < headings.length; index++) {
        const score = headingOverlapScore(headings[index].h || '', sentence);
        if (score > 0 && (!best || score > best.score)) {
            best = {index, score};
        }
    }
    if (!best) {
        return null;
    }
    return {contentPart: 'h2', index: best.index, tag: 'p'};
}

function phraseInText(haystack: string, phrase: string): boolean {
    const needle = normalizeBriefPhrase(phrase);
    if (!needle || needle.length < 4) {
        return false;
    }
    const text = normalizeBriefPhrase(haystack);
    if (!text) {
        return false;
    }
    if (textContainsPhrase(haystack, phrase) || (needle.length >= 12 && text.includes(needle.slice(0, Math.min(48, needle.length))))) {
        return true;
    }
    const distinctive = (phrase.match(/\d+(?:[.,]\d+)?\s*%?/g) || []).map((item) => item.replace(/\s+/g, ''));
    const compact = text.replace(/\s+/g, '');
    return distinctive.some((token) => token.length >= 2 && compact.includes(token.toLowerCase()));
}

export function livePhraseInArticle(article: ArticleLike, phrase: string): boolean {
    for (let level = 1; level <= 6; level++) {
        const blocks = article[('h' + level) as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'] || [];
        for (const block of blocks) {
            if (phraseInText(block.p || '', phrase) || phraseInText(block.h || '', phrase)) {
                return true;
            }
        }
    }
    return (article.questions || []).some((item) => phraseInText(item.question || '', phrase));
}

function headingInArticle(article: ArticleLike, heading: string): boolean {
    const needle = normalizeBriefPhrase(heading);
    if (!needle) {
        return false;
    }
    for (let level = 2; level <= 6; level++) {
        const blocks = article[('h' + level) as 'h2' | 'h3' | 'h4' | 'h5' | 'h6'] || [];
        for (const block of blocks) {
            const text = normalizeBriefPhrase(block.h || '');
            if (text === needle || (needle.length >= 12 && (text.includes(needle) || needle.includes(text)))) {
                return true;
            }
        }
    }
    return false;
}

function alreadyInOutline(article: ArticleLike, question: string): boolean {
    if (headingInArticle(article, question)) {
        return true;
    }
    return (article.questions || []).some((item) => {
        const text = String(item.question || '');
        return textContainsPhrase(text, question) || textContainsPhrase(question, text);
    });
}

function uniqueSentences(values: string[], max: number): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const raw of values) {
        const sentence = asWorkInSentence(raw);
        const key = normalizeBriefPhrase(sentence);
        if (!sentence || !key || seen.has(key)) {
            continue;
        }
        seen.add(key);
        result.push(sentence);
        if (result.length >= max) {
            break;
        }
    }
    return result;
}

function extractNumberedFacts(answer: AiAnswerSnapshot): string[] {
    const facts: string[] = [];
    const add = (raw: string) => {
        const sentence = asWorkInSentence(humanizeAiAnswerMarkdown(raw));
        if (!sentence || !isConcreteFact(sentence) || wordCount(sentence) > 42) {
            return;
        }
        facts.push(sentence);
    };
    for (const snippet of answer.citedSnippets || []) {
        add(snippet);
    }
    const readable = liveAnswerBody(answer);
    for (const part of splitLiveAnswerSentences(readable)) {
        add(part);
    }
    return facts;
}

export function planLiveAnswerWorkIn(
    answer: AiAnswerSnapshot | null | undefined,
    article: Article | ArticleLike
): LiveWorkInPlan {
    const empty: LiveWorkInPlan = {lead: '', introFacts: [], headingFacts: [], questions: []};
    const body = liveAnswerBody(answer);
    if (!answer || !body) {
        return empty;
    }

    const used = new Set<string>();
    const remember = (text: string): boolean => {
        const key = normalizeBriefPhrase(text);
        if (!key || used.has(key)) {
            return false;
        }
        used.add(key);
        return true;
    };

    const leadRaw = extractLiveLeadClaim(body);
    const lead = leadRaw && !livePhraseInArticle(article, leadRaw) && remember(leadRaw) ? leadRaw : '';

    const citedClaims = (answer.citedSnippets || [])
        .map((item) => asWorkInSentence(humanizeAiAnswerMarkdown(item)))
        .filter((item) => {
            const words = wordCount(item);
            return words >= 5 && words <= 42 && !looksLikeLiveQuestion(item);
        });

    const facts = uniqueSentences([...extractNumberedFacts(answer), ...citedClaims], MAX_LIVE_WORK_IN_FACTS)
        .filter((sentence) => {
            if (lead && normalizeBriefPhrase(sentence) === normalizeBriefPhrase(lead)) {
                return false;
            }
            if (livePhraseInArticle(article, sentence)) {
                return false;
            }
            return remember(sentence);
        });

    const introFacts: string[] = [];
    const headingFacts: Array<{index: number; sentence: string}> = [];
    for (const sentence of facts) {
        const related = findRelatedHeadingTarget(article, sentence);
        if (related) {
            headingFacts.push({index: related.index, sentence});
        } else {
            introFacts.push(sentence);
        }
    }

    const questions = extractLiveQuestions(body)
        .filter((question) => !alreadyInOutline(article, question) && remember(question));

    const budget = MAX_LIVE_WORK_IN - (lead ? 1 : 0) - questions.length;
    const keptFacts = [...introFacts, ...headingFacts.map((item) => item.sentence)].slice(0, Math.max(0, budget));
    const introSet = new Set(introFacts);
    const nextIntro: string[] = [];
    const nextHeading: Array<{index: number; sentence: string}> = [];
    for (const sentence of keptFacts) {
        if (introSet.has(sentence)) {
            nextIntro.push(sentence);
        } else {
            const row = headingFacts.find((item) => item.sentence === sentence);
            if (row) {
                nextHeading.push(row);
            }
        }
    }

    return {
        lead,
        introFacts: nextIntro,
        headingFacts: nextHeading,
        questions,
    };
}

export function liveWorkInCount(plan: LiveWorkInPlan): number {
    return (plan.lead ? 1 : 0) + plan.introFacts.length + plan.headingFacts.length + plan.questions.length;
}

export function markWorkedInPlatform(platforms: string[] | undefined, platform: string): string[] {
    const next = [...(platforms || [])];
    if (!next.some((item) => item === platform)) {
        next.push(platform);
    }
    return next.slice(0, 8);
}

export const MAX_LIVE_ANALYSIS_QUESTIONS = 24;

/** Questions from live AI-answer markdown for the keyword-analysis table (tag z AI chatu). */
export function questionsFromLiveAnswers(
    answers?: Array<{markdown?: string} | null | undefined> | null
): Array<{question: string; source: 'mentions'}> {
    const found: Array<{question: string; source: 'mentions'}> = [];
    const seen = new Set<string>();
    for (const answer of answers || []) {
        if (!answer?.markdown) {
            continue;
        }
        for (const question of extractLiveQuestions(answer.markdown, MAX_LIVE_ANALYSIS_QUESTIONS)) {
            const key = normalizeBriefPhrase(question);
            if (!key || seen.has(key)) {
                continue;
            }
            seen.add(key);
            found.push({question, source: 'mentions'});
            if (found.length >= MAX_LIVE_ANALYSIS_QUESTIONS) {
                return found;
            }
        }
    }
    return found;
}
